/**
 * The "Easy button" family — see memory-bank/plans/easy-buttons.md.
 *
 * Two buttons today, one shared contract. Every one of these previews returns
 * a `ProposalEnvelope` carrying the exclusion ledger, and the arithmetic
 * closes: `considered === sum(excluded counts) + eligible`. The server asserts
 * it; the UI renders it. A preview that showed only its successes would be the
 * lasso footer's "0 missing coordinates" bug all over again — the coordinator
 * sees a small number and cannot tell whether the tool is broken or the data
 * is.
 *
 * Zero-count exclusions are dropped server-side, so an empty `excluded` array
 * means "nothing was dropped", not "we didn't check".
 */
import client from '@/api/client';

// ---------------------------------------------------------------------------
// Shared envelope
// ---------------------------------------------------------------------------

export type Exclusion = {
  /** Stable machine code, e.g. "ALREADY_ASSIGNED". Safe to switch on. */
  code: string;
  count: number;
  /** Human sentence. Render as-is — it is written to be read by a coordinator. */
  label: string;
};

export type ProposalEnvelope<TItem = Record<string, unknown>> = {
  considered: number;
  excluded: Exclusion[];
  eligible: number;
  planned: number;
  not_planned: number;
  /** A cap truncated the batch. Never silent — say so on screen. */
  capped: boolean;
  items: TItem[];
};

// ---------------------------------------------------------------------------
// #1 — Staff the weekend
// ---------------------------------------------------------------------------

export type StaffingItem = {
  route_id: number;
  route_name: string;
  precinct_num: string | null;
  subregion: string | null;
  door_count: number;
  est_hours: number;
  currently_assigned_to: number | null;
  /** Who the plan proposes (or the coordinator chose). null = leave open. */
  assigned_to: number | null;
  /**
   * The dropdown's options for THIS row, computed server-side from each
   * canvasser's region scope. Rendering anything outside this list would offer
   * a pair the commit rejects.
   */
  eligible_canvasser_ids: number[];
};

export type CanvasserLoad = {
  id: number;
  full_name: string | null;
  email: string | null;
  routes: number;
  doors: number;
  scoped: boolean;
};

export type StaffingPreviewRequest = {
  route_ids?: number[] | null;
  canvasser_ids?: number[] | null;
  balance?: 'round_robin' | 'by_doors';
  include_assigned?: boolean;
  /** Batch size. The tenant can hold thousands of unassigned routes. */
  limit?: number;
  /** The edited map, to re-balance the preview around the coordinator's picks. */
  assignments?: Record<number, number>;
};

export type StaffingPreviewResponse = {
  proposal: ProposalEnvelope<StaffingItem>;
  /** route_id -> canvasser_id. This is what commit consumes. */
  assignments: Record<string, number>;
  canvassers: CanvasserLoad[];
};

export type StaffingCommitResponse = {
  assigned: number;
  unassigned: number;
  shifts_created: number;
  routes: Array<{
    id: number;
    name: string;
    assigned_canvasser_id: number | null;
    door_count: number;
  }>;
};

/** Propose who walks what. Writes nothing. */
export async function previewStaffing(
  params: StaffingPreviewRequest,
  signal?: AbortSignal,
): Promise<StaffingPreviewResponse> {
  const res = await client.post('/routes/staff/preview', params, { signal });
  return res.data;
}

/**
 * Write the edited map.
 *
 * Unlike autoplan's commit, this does NOT re-plan — the map is authoritative,
 * which is what makes the preview editable. Every pair is still re-validated
 * server-side, so a hand-edited pair earns no extra trust. `0` unassigns.
 */
export async function commitStaffing(
  assignments: Record<number, number>,
  opts: { create_shifts?: boolean; shift_date?: string | null } = {},
): Promise<StaffingCommitResponse> {
  const res = await client.post('/routes/staff', {
    assignments,
    create_shifts: opts.create_shifts ?? false,
    shift_date: opts.shift_date ?? null,
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// #4 — Close out the day
// ---------------------------------------------------------------------------

export type StaleShiftItem = {
  shift_id: number;
  canvasser_id: number;
  canvasser_name: string | null;
  route_id: number;
  route_name: string | null;
  started_at: string | null;
  hours_open: number | null;
  /**
   * Knocks logged on this shift. The column that stops a coordinator sweeping
   * somebody who is genuinely still out.
   */
  knock_count: number;
  selected: boolean;
};

export type CloseStaleResponse = {
  proposal: ProposalEnvelope<StaleShiftItem>;
  shift_ids: number[];
  stale_hours: number;
  cutoff: string;
  /** Only present on commit. */
  closed?: number | null;
};

export async function previewCloseStale(
  params: { stale_hours?: number; shift_ids?: number[] | null },
  signal?: AbortSignal,
): Promise<CloseStaleResponse> {
  const res = await client.post('/canvass/shifts/close-stale/preview', params, {
    signal,
  });
  return res.data;
}

/**
 * Close the swept shifts.
 *
 * Eligibility is re-derived server-side, so a volunteer who ended their shift
 * between preview and press is not re-closed. Swept rows are flagged
 * `ended_by_sweep` — their `ended_at` means "closed at", not "ended at".
 */
export async function commitCloseStale(params: {
  stale_hours?: number;
  shift_ids?: number[] | null;
}): Promise<CloseStaleResponse> {
  const res = await client.post('/canvass/shifts/close-stale', params);
  return res.data;
}

// ---------------------------------------------------------------------------
// Shared error shaping
// ---------------------------------------------------------------------------

/** Turn an axios error into one sentence a coordinator can act on. */
export function easyButtonError(err: unknown, fallback: string): string {
  const resp = (err as any)?.response;
  if (resp?.status === 403) {
    return (
      resp?.data?.detail ||
      'You do not have permission for this. It requires a coordinator or above.'
    );
  }
  const detail = resp?.data?.detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors arrive as a list of objects; joining beats
    // rendering "[object Object]" at a coordinator.
    return detail.map((d: any) => d?.msg).filter(Boolean).join('; ') || fallback;
  }
  return detail || (err as any)?.message || fallback;
}
